import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import { CTA, waInspectorHref } from "@/lib/business";
import { trackEvent } from "@/lib/analytics";
import { isSnapshot } from "@/lib/seo";

const SERVICE_LINKS = [
  { label: "Snagging inspection", href: "/property-snagging-dubai" },
  { label: "Pre-handover inspection", href: "/handover-inspection-dubai" },
  { label: "DLP / warranty inspection", href: "/dlp-warranty-inspection-dubai" },
  { label: "Resale inspection", href: "/resale-property-inspection-dubai" },
  { label: "Reinspection", href: "/reinspection-dubai" },
  { label: "Commercial inspection", href: "/commercial-property-inspection-dubai" },
  { label: "Defect Cost Estimation", href: "/defect-cost-estimation-dubai" },
  { label: "Site Inspection", href: "/site-inspection-dubai" },
  { label: "Renovation snagging", href: "/renovation-snagging-dubai" },
  { label: "SPA based inspection", href: "/spa-inspection-dubai" },
  { label: "MEP inspections", href: "/mep-inspection-dubai" },
  { label: "Mold inspections", href: "/mold-inspection-dubai" },
  { label: "Pressure & smoke testing", href: "/pressure-smoke-testing-dubai" },
  { label: "Electrical load & DB testing", href: "/electrical-load-db-testing-dubai" },
  { label: "HVAC duct inspections", href: "/hvac-duct-inspection-dubai" },
];

const LINKS = [
  { label: "Services", dropdown: true },
  { label: "Communities", href: "/property-snagging-dubai-communities", page: true },
  { label: "Guides", href: "/guides", page: true },
  { label: "Sample report", href: "/sample-snagging-report", page: true },
  { label: "Inspection Tools", href: "/inspection-tools", page: true },
  { label: "Pricing", href: "/#pricing" },
  { label: "About", href: "/about-fedari", page: true },

  { label: "Partners", href: "/partners", page: true },
  { label: "Contact", href: "/contact", page: true },
];

function WhatsAppIcon({ size = 22 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden>
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm5.8 14.16c-.25.7-1.44 1.33-2 1.42-.53.08-1.2.11-1.94-.12-.45-.14-1.02-.33-1.76-.65-3.1-1.34-5.12-4.46-5.28-4.67-.16-.21-1.26-1.67-1.26-3.19s.8-2.27 1.08-2.58c.28-.31.61-.39.82-.39l.59.01c.19 0 .44-.07.69.53.25.6.85 2.07.92 2.22.08.15.13.33.02.53-.1.21-.16.33-.31.5-.16.18-.33.4-.47.53-.16.16-.32.32-.14.63.18.31.8 1.32 1.72 2.14 1.18 1.05 2.18 1.38 2.49 1.53.31.16.49.13.67-.08.18-.21.77-.9.98-1.21.21-.31.41-.26.69-.16.28.11 1.79.84 2.1.99.31.16.51.23.59.36.08.13.08.74-.17 1.44Z" />
    </svg>
  );
}

function ServicesDropdown() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-haspopup="true" data-testid="nav-services"
        className="text-bodytext hover:text-blue transition-colors py-1.5 whitespace-nowrap inline-flex items-center gap-1 font-medium">
        Services <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50">
          <div className="bg-white border border-line rounded-[12px] shadow-[0_16px_40px_-12px_rgba(13,17,23,.18)] py-2 w-[250px]">
            {SERVICE_LINKS.map((s) => (
              <Link key={s.href} to={s.href} onClick={() => setOpen(false)} data-testid={`nav-service-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="block px-4 py-2.5 text-[0.85rem] text-bodytext hover:text-blue hover:bg-soft transition-colors">{s.label}</Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Header({ onBook, onQuote }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const primary = () => { trackEvent("click_primary_cta", { location: "header" }); onQuote(); };
  const wa = (loc) => trackEvent("whatsapp_click", { location: loc });

  useEffect(() => {
    if (isSnapshot()) return;
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-2 focus:left-2 focus:bg-blue focus:text-white focus:px-4 focus:py-2 focus:rounded-md" data-testid="skip-to-content">Skip to content</a>
      <div className="bg-navy text-white text-center text-[0.8rem] py-2 px-4" data-testid="announcement-bar">
        Same-week inspections are subject to availability.{" "}
        <button onClick={primary} className="text-[#6ea2ff] font-medium hover:underline">Check availability →</button>
      </div>

      <header className={`sticky top-0 z-50 border-b border-line transition-colors ${scrolled ? "bg-white/80 backdrop-blur-xl backdrop-saturate-150" : "bg-white"}`} data-testid="navbar">
        <div className="wrap flex items-center justify-between h-[104px] lg:h-[160px] gap-6">
          <Link to="/" aria-label="Fedari Inspection home" className="flex-none shrink-0"><Logo className="flex-none shrink-0" imgClass="h-[5rem] lg:h-[9rem]" /></Link>
          <nav className="hidden lg:flex gap-3 xl:gap-6 items-center text-[0.85rem] xl:text-[0.92rem] font-medium ml-auto mr-2" aria-label="Primary">
            {LINKS.map((l) => (
              l.dropdown ? (
                <ServicesDropdown key="services" />
              ) : l.page ? (
                <Link key={l.href} to={l.href} data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  aria-current={pathname === l.href ? "page" : undefined}
                  className={`transition-colors py-1.5 whitespace-nowrap ${pathname === l.href ? "text-blue font-semibold" : "text-bodytext hover:text-blue"}`}>{l.label}</Link>
              ) : (
                <a key={l.href} href={l.href} data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="text-bodytext hover:text-blue transition-colors py-1.5 whitespace-nowrap">{l.label}</a>
              )
            ))}
          </nav>
          <div className="flex items-center gap-2.5">
            <a href={waInspectorHref} target="_blank" rel="noopener noreferrer" onClick={() => wa("header")} data-testid="nav-whatsapp"
              className="hidden sm:inline-flex lg:hidden 2xl:inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] border border-line text-ink text-sm font-semibold hover:border-wa hover:text-wa transition-colors">
              <WhatsAppIcon size={16} /> WhatsApp
            </a>
            <button onClick={primary} data-testid="nav-quote-btn"
              className="inline-flex items-center gap-2 bg-blue text-white px-5 py-2.5 rounded-[10px] text-sm font-semibold whitespace-nowrap hover:bg-blue-600 hover:-translate-y-px transition-all shadow-[0_1px_2px_rgba(10,108,255,.24)]">
              <span className="hidden 2xl:inline">{CTA.primaryShort}</span><span className="hidden lg:inline 2xl:hidden">{CTA.primaryNav}</span><span className="lg:hidden">{CTA.primaryMobile}</span> <ArrowRight size={15} />
            </button>
            <button onClick={() => setOpen(true)} className="xl:hidden p-2" data-testid="mobile-menu-btn" aria-label="Open menu"><Menu size={22} /></button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-white px-6 pt-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] xl:hidden overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]" data-testid="mobile-nav">
            <div className="sticky top-0 z-10 -mx-6 px-6 pt-0 pb-4 bg-white flex items-center justify-between mb-6">
              <Logo />
              <button onClick={() => setOpen(false)} className="p-2" aria-label="Close menu" data-testid="mobile-menu-close"><X size={24} /></button>
            </div>
            <nav className="flex flex-col gap-2" aria-label="Mobile">
              {LINKS.map((l) => (
                l.dropdown ? (
                  <div key="services">
                    <div className="eyebrow pt-5 pb-1">Services</div>
                    {SERVICE_LINKS.map((s) => (
                      <Link key={s.href} to={s.href} onClick={() => setOpen(false)} data-testid={`mobile-service-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`} className="text-lg font-display py-2.5 pl-4 border-b border-line block">{s.label}</Link>
                    ))}
                  </div>
                ) : l.page ? (
                  <Link key={l.href} to={l.href} onClick={() => setOpen(false)} aria-current={pathname === l.href ? "page" : undefined}
                    className={`text-xl font-display py-3 border-b border-line ${pathname === l.href ? "text-blue" : ""}`}>{l.label}</Link>
                ) : (
                  <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-xl font-display py-3 border-b border-line">{l.label}</a>
                )
              ))}
            </nav>
            <button onClick={() => { setOpen(false); primary(); }} className="mt-8 w-full bg-blue text-white py-4 rounded-[10px] font-semibold">{CTA.primary}</button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile sticky action bar */}
      <div className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-white/95 backdrop-blur border-t border-line px-3 pt-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] flex gap-2.5" data-testid="mobile-action-bar">
        <button onClick={primary} data-testid="mobile-primary-cta" className="flex-1 bg-blue text-white py-3 rounded-[10px] text-sm font-semibold inline-flex items-center justify-center gap-1.5">{CTA.primaryMobile}</button>
        <a href={waInspectorHref} target="_blank" rel="noopener noreferrer" onClick={() => wa("mobile-bar")} data-testid="mobile-whatsapp" className="w-14 grid place-items-center rounded-[10px] bg-wa text-white flex-none" aria-label="WhatsApp Fedari"><WhatsAppIcon size={22} /></a>
      </div>

      <a href={waInspectorHref} target="_blank" rel="noopener noreferrer" onClick={() => wa("fab")} data-testid="whatsapp-fab" aria-label="Chat with Fedari Inspection on WhatsApp"
        className="hidden lg:grid fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-wa text-white place-items-center shadow-[0_12px_28px_-8px_rgba(37,211,102,.6)] hover:scale-105 transition-transform">
        <WhatsAppIcon size={28} />
      </a>
    </>
  );
}
